import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { Platform, View, Text, StyleSheet } from 'react-native';
import { useSearchParams } from 'react-router-dom';
import {
  AlertCircle,
  Calendar,
  Camera,
  Check,
  CheckCircle2,
  Clock,
  Home,
  Loader2,
  MessageSquare,
  Phone,
  RotateCcw,
  SkipForward,
  Upload,
  User,
} from '../lib/icons';
import { supabase } from '../lib/supabase';
import { formatVisitDurationLabel } from '../lib/visitDuration';

interface Visit {
  id: string;
  visit_type: 'maintenance' | 'service' | 'walkthrough';
  status: 'requested' | 'proposed' | 'scheduled' | 'in_progress' | 'completed' | 'cancelled' | 'no_show';
  scheduled_date: string | null;
  estimated_duration_minutes: number | null;
  actual_start_time: string | null;
  actual_end_time: string | null;
  contractor_notes: string | null;
  provider_notes: string | null;
  homeowner_notes: string | null;
  homeowner_submitted_at: string | null;
  address: string;
  city: string;
  state: string;
  zip_code: string;
  owner_name: string;
  owner_phone: string | null;
  total_tasks: number;
  completed_tasks: number;
}

interface VisitTask {
  id: string;
  task_title: string;
  task_description: string | null;
  status: 'pending' | 'completed' | 'skipped';
  estimated_duration_minutes: number | null;
  actual_duration_minutes: number | null;
  requested_by_homeowner: boolean | null;
  homeowner_notes: string | null;
}

interface VisitPhoto {
  id: string;
  photo_url: string;
  description: string | null;
  photo_type: 'before' | 'during' | 'after' | 'general' | 'issue' | 'completion';
  taken_at?: string;
}

const formatDateTime = (dateString: string | null) => {
  if (!dateString) return 'TBD';
  return new Date(dateString).toLocaleString('en-US', {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatStatus = (status: Visit['status']) => status.replace('_', ' ');

const formatDuration = (minutes: number | null | undefined) => {
  if (typeof minutes !== 'number' || Number.isNaN(minutes) || minutes <= 0) {
    return 'TBD';
  }
  return formatVisitDurationLabel(minutes);
};


const NativeFallback = () => (
  <View style={nativeFallbackStyles.container}>
    <Text style={nativeFallbackStyles.title}>Open this link in a browser</Text>
    <Text style={nativeFallbackStyles.body}>
      The contractor visit page is only available on the web. Please open the email link
      from your phone or computer's browser.
    </Text>
  </View>
);

const nativeFallbackStyles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 24,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#f9fafb',
  },
  title: {
    fontSize: 18,
    fontWeight: '600',
    color: '#111827',
    marginBottom: 8,
    textAlign: 'center',
  },
  body: {
    fontSize: 14,
    color: '#4b5563',
    textAlign: 'center',
    maxWidth: 320,
  },
});

const ContractorVisitDetail = () => {
  if (Platform.OS !== 'web') {
    return <NativeFallback />;
  }
  return <ContractorVisitDetailWeb />;
};

const ContractorVisitDetailWeb = () => {
  const [searchParams] = useSearchParams();
  const contractorToken = searchParams.get('token');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [visit, setVisit] = useState<Visit | null>(null);
  const [tasks, setTasks] = useState<VisitTask[]>([]);
  const [photos, setPhotos] = useState<VisitPhoto[]>([]);
  const [updatingTaskId, setUpdatingTaskId] = useState<string | null>(null);
  const [notesDraft, setNotesDraft] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const [notesMessage, setNotesMessage] = useState<string | null>(null);
  const [notesMessageType, setNotesMessageType] = useState<'success' | 'error'>('success');
  const [photoType, setPhotoType] = useState<VisitPhoto['photo_type']>('general');
  const [photoDescription, setPhotoDescription] = useState('');
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [uploadMessage, setUploadMessage] = useState<string | null>(null);
  const [uploadMessageType, setUploadMessageType] = useState<'success' | 'error'>('success');

  useEffect(() => {
    const fetchVisitData = async () => {
      if (!contractorToken) {
        setError('A contractor access token is required.');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const { data, error: invokeError } = await supabase.functions.invoke('get-contractor-visit', {
          body: {
            token: contractorToken,
          },
        });

        if (invokeError) throw invokeError;
        if (!data?.visit) {
          throw new Error('Visit data was not returned.');
        }

        const visitData = data.visit as Visit;
        setVisit(visitData);
        setNotesDraft(visitData.provider_notes || '');
        setTasks((data.tasks || []) as VisitTask[]);
        setPhotos((data.photos || []) as VisitPhoto[]);
      } catch (fetchError: any) {
        console.error('Error loading contractor visit data:', fetchError);
        setError(fetchError.message || 'Unable to load this visit.');
      } finally {
        setLoading(false);
      }
    };

    fetchVisitData();
  }, [contractorToken]);

  const totalTasksCount = useMemo(
    () => (tasks.length > 0 ? tasks.length : visit?.total_tasks || 0),
    [tasks, visit?.total_tasks],
  );
  const completedTasksCount = useMemo(
    () => tasks.filter((task) => task.status === 'completed').length,
    [tasks],
  );
  const progressPercent = useMemo(() => {
    if (!totalTasksCount) return 0;
    return Math.round((completedTasksCount / totalTasksCount) * 100);
  }, [completedTasksCount, totalTasksCount]);

  const updateTaskStatus = async (taskId: string, status: VisitTask['status']) => {
    if (!contractorToken) return;

    setUpdatingTaskId(taskId);
    try {
      const { data, error: invokeError } = await supabase.functions.invoke('update-contractor-visit', {
        body: {
          token: contractorToken,
          action: 'task_status',
          taskId,
          status,
        },
      });

      if (invokeError) throw invokeError;
      const updatedTask = data?.task as VisitTask | undefined;
      if (!updatedTask) {
        throw new Error('Task update failed.');
      }

      setTasks((prev) => prev.map((task) => (task.id === updatedTask.id ? updatedTask : task)));
      setError(null);
    } catch (updateError: any) {
      console.error('Error updating task status:', updateError);
      setError(updateError.message || 'Could not update task status.');
    } finally {
      setUpdatingTaskId(null);
    }
  };

  const saveContractorNotes = async () => {
    if (!contractorToken) return;

    setSavingNotes(true);
    setNotesMessage(null);
    try {
      const { data, error: invokeError } = await supabase.functions.invoke('update-contractor-visit', {
        body: {
          token: contractorToken,
          action: 'provider_notes',
          providerNotes: notesDraft,
        },
      });

      if (invokeError) throw invokeError;

      const updatedNotes = (data?.visit?.provider_notes as string | null | undefined) ?? null;
      setVisit((prev) => (prev ? { ...prev, provider_notes: updatedNotes } : prev));
      setNotesDraft(updatedNotes || '');
      setNotesMessageType('success');
      setNotesMessage('Visit notes saved.');
      setError(null);
    } catch (notesError: any) {
      console.error('Error saving contractor notes:', notesError);
      setNotesMessageType('error');
      setNotesMessage(notesError.message || 'Could not save visit notes.');
    } finally {
      setSavingNotes(false);
    }
  };

  const handlePhotoUpload = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !visit || !contractorToken) return;

    setUploadMessage(null);
    setUploadingPhoto(true);

    try {
      const form = new FormData();
      form.append('token', contractorToken);
      form.append('file', file);
      form.append('photoType', photoType);
      if (photoDescription.trim()) {
        form.append('description', photoDescription.trim());
      }

      const { data, error } = await supabase.functions.invoke('upload-contractor-visit-photo', {
        body: form,
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      if (data?.photo) {
        setPhotos((prev) => [...prev, data.photo as VisitPhoto]);
      }

      setPhotoDescription('');
      setPhotoType('general');
      setUploadMessageType('success');
      setUploadMessage('Photo uploaded.');
    } catch (uploadErr: any) {
      console.error('Error uploading visit photo:', uploadErr);
      setUploadMessageType('error');
      setUploadMessage(uploadErr?.message || 'Could not upload photo.');
    } finally {
      setUploadingPhoto(false);
      event.target.value = '';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-black" />
      </div>
    );
  }

  if (!visit || error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white shadow rounded-xl p-6 text-center">
          <AlertCircle className="w-10 h-10 text-red-500 mx-auto mb-3" />
          <h1 className="text-lg font-semibold text-gray-900">Visit unavailable</h1>
          <p className="text-sm text-gray-600 mt-2">{error || 'This visit could not be found.'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        <div className="bg-white shadow rounded-xl p-5 sm:p-6">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-black text-white capitalize">
              {formatStatus(visit.status)}
            </span>
            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700 capitalize">
              {visit.visit_type}
            </span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900">{visit.address}</h1>
          <p className="text-sm text-gray-600 mt-1">
            {visit.city}, {visit.state} {visit.zip_code}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <section className="bg-white shadow rounded-xl p-5 sm:p-6 space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">Visit Timing</h2>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <Calendar className="w-4 h-4 text-gray-500 mt-0.5" />
                <div>
                  <p className="text-xs uppercase tracking-wide text-gray-500">Scheduled start</p>
                  <p className="text-sm text-gray-900">{formatDateTime(visit.scheduled_date)}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Clock className="w-4 h-4 text-gray-500 mt-0.5" />
                <div>
                  <p className="text-xs uppercase tracking-wide text-gray-500">Estimated duration</p>
                  <p className="text-sm text-gray-900">{formatDuration(visit.estimated_duration_minutes)}</p>
                </div>
              </div>
              {visit.actual_start_time && (
                <div className="flex items-start gap-3">
                  <Clock className="w-4 h-4 text-green-600 mt-0.5" />
                  <div>
                    <p className="text-xs uppercase tracking-wide text-gray-500">Actual check-in</p>
                    <p className="text-sm text-gray-900">{formatDateTime(visit.actual_start_time)}</p>
                  </div>
                </div>
              )}
              {visit.actual_end_time && (
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5" />
                  <div>
                    <p className="text-xs uppercase tracking-wide text-gray-500">Actual clock-out</p>
                    <p className="text-sm text-gray-900">{formatDateTime(visit.actual_end_time)}</p>
                  </div>
                </div>
              )}
            </div>
          </section>

          <section className="bg-white shadow rounded-xl p-5 sm:p-6 space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">Homeowner Contact</h2>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <User className="w-4 h-4 text-gray-500 mt-0.5" />
                <div>
                  <p className="text-xs uppercase tracking-wide text-gray-500">Name</p>
                  <p className="text-sm text-gray-900">{visit.owner_name}</p>
                </div>
              </div>
              {visit.owner_phone && (
                <div className="flex items-start gap-3">
                  <Phone className="w-4 h-4 text-gray-500 mt-0.5" />
                  <div>
                    <p className="text-xs uppercase tracking-wide text-gray-500">Phone</p>
                    <a href={`tel:${visit.owner_phone}`} className="text-sm text-blue-700 hover:text-blue-800">
                      {visit.owner_phone}
                    </a>
                  </div>
                </div>
              )}
              {visit.homeowner_notes && (
                <div className="bg-blue-50 rounded-lg p-3">
                  <p className="text-sm font-medium text-blue-900 flex items-center gap-1.5">
                    <MessageSquare className="w-4 h-4" />
                    Homeowner notes
                  </p>
                  <p className="text-sm text-blue-800 mt-1">{visit.homeowner_notes}</p>
                  {visit.homeowner_submitted_at && (
                    <p className="text-xs text-blue-700 mt-2">
                      Submitted {new Date(visit.homeowner_submitted_at).toLocaleDateString()}
                    </p>
                  )}
                </div>
              )}
            </div>
          </section>
        </div>

        <section className="bg-white shadow rounded-xl p-5 sm:p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">Need Help from Honeydone?</h2>
          <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
            <p className="text-sm text-blue-900">
              If you need anything during this visit, contact Honeydone and our team will help right away.
            </p>
            <div className="mt-3 space-y-2">
              <p className="text-sm text-blue-900">
                Phone:{' '}
                <a href="tel:6162363800" className="font-medium text-blue-700 hover:text-blue-800">
                  616 236 3800
                </a>
              </p>
              <p className="text-sm text-blue-900">
                Email:{' '}
                <a href="mailto:service@honeydone.com" className="font-medium text-blue-700 hover:text-blue-800">
                  service@honeydone.com
                </a>
              </p>
            </div>
          </div>
        </section>

        <section className="bg-white shadow rounded-xl p-5 sm:p-6 space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">Scope of Work</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs uppercase tracking-wide text-gray-500">Total tasks</p>
              <p className="text-xl font-semibold text-gray-900">{totalTasksCount}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs uppercase tracking-wide text-gray-500">Completed</p>
              <p className="text-xl font-semibold text-green-600">{completedTasksCount}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs uppercase tracking-wide text-gray-500">Progress</p>
              <p className="text-xl font-semibold text-gray-900">{progressPercent}%</p>
            </div>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2.5">
            <div className="bg-green-600 h-2.5 rounded-full transition-all" style={{ width: `${progressPercent}%` }} />
          </div>

          <div className="space-y-3 pt-1">
            {tasks.length === 0 ? (
              <p className="text-sm text-gray-600">No tasks have been added yet.</p>
            ) : (
              tasks.map((task) => {
                const isCompleted = task.status === 'completed';
                const isSkipped = task.status === 'skipped';
                const isUpdating = updatingTaskId === task.id;

                return (
                  <div
                    key={task.id}
                    className={`rounded-lg border-l-4 border border-l-4 p-4 transition-colors ${
                      isCompleted
                        ? 'border-green-400 border-l-green-500 bg-green-50'
                        : isSkipped
                          ? 'border-gray-200 border-l-gray-400 bg-gray-50'
                          : 'border-gray-200 border-l-gray-300 bg-white'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div
                        className={`mt-0.5 flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${
                          isCompleted
                            ? 'border-green-500 bg-green-500'
                            : isSkipped
                              ? 'border-gray-400 bg-gray-200'
                              : 'border-gray-300 bg-white'
                        }`}
                      >
                        {isCompleted && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <p
                            className={`font-medium leading-snug ${
                              isCompleted ? 'text-green-900 line-through decoration-green-400' : isSkipped ? 'text-gray-400 line-through' : 'text-gray-900'
                            }`}
                          >
                            {task.task_title}
                          </p>
                          {isSkipped && (
                            <span className="flex-shrink-0 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-200 text-gray-600">
                              Skipped
                            </span>
                          )}
                          {isCompleted && (
                            <span className="flex-shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">
                              <CheckCircle2 className="w-3 h-3" />
                              Done
                            </span>
                          )}
                        </div>
                        {task.task_description && (
                          <p className={`text-sm mt-1 ${isCompleted || isSkipped ? 'text-gray-400' : 'text-gray-600'}`}>
                            {task.task_description}
                          </p>
                        )}

                        <div className="flex flex-wrap gap-2 mt-2">
                          {task.estimated_duration_minutes && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs bg-gray-100 text-gray-600">
                              {task.estimated_duration_minutes} min est.
                            </span>
                          )}
                          {task.actual_duration_minutes && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs bg-green-100 text-green-800">
                              {task.actual_duration_minutes} min actual
                            </span>
                          )}
                          {task.requested_by_homeowner && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs bg-blue-100 text-blue-800">
                              Homeowner request
                            </span>
                          )}
                        </div>

                        {task.homeowner_notes && (
                          <div className="mt-3 bg-blue-50 rounded-md p-2.5">
                            <p className="text-xs uppercase tracking-wide text-blue-700">Homeowner context</p>
                            <p className="text-sm text-blue-800 mt-1">{task.homeowner_notes}</p>
                          </div>
                        )}

                        <div className="mt-4 flex flex-wrap gap-2">
                          {!isCompleted && (
                            <button
                              type="button"
                              onClick={() => updateTaskStatus(task.id, 'completed')}
                              disabled={isUpdating}
                              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-green-600 text-white text-sm font-medium hover:bg-green-700 active:bg-green-800 disabled:opacity-60 transition-colors shadow-sm"
                            >
                              {isUpdating ? (
                                <>
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                  Saving…
                                </>
                              ) : (
                                <>
                                  <Check className="w-4 h-4" strokeWidth={2.5} />
                                  Mark Complete
                                </>
                              )}
                            </button>
                          )}
                          {isCompleted && (
                            <button
                              type="button"
                              onClick={() => updateTaskStatus(task.id, 'pending')}
                              disabled={isUpdating}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-300 text-gray-600 text-xs font-medium hover:bg-gray-50 disabled:opacity-60 transition-colors"
                            >
                              {isUpdating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
                              Undo
                            </button>
                          )}
                          {!isSkipped && (
                            <button
                              type="button"
                              onClick={() => updateTaskStatus(task.id, 'skipped')}
                              disabled={isUpdating}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 text-gray-500 text-xs hover:bg-gray-50 disabled:opacity-60 transition-colors"
                            >
                              <SkipForward className="w-3.5 h-3.5" />
                              Skip
                            </button>
                          )}
                          {isSkipped && (
                            <button
                              type="button"
                              onClick={() => updateTaskStatus(task.id, 'pending')}
                              disabled={isUpdating}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-300 text-gray-600 text-xs font-medium hover:bg-gray-50 disabled:opacity-60 transition-colors"
                            >
                              {isUpdating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
                              Restore
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </section>

        <section className="bg-white shadow rounded-xl p-5 sm:p-6 space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">Visit Notes & Photos</h2>
          <div className="bg-blue-50 rounded-lg p-3">
            <p className="text-sm font-medium text-blue-900 flex items-center gap-1.5">
              <MessageSquare className="w-4 h-4" />
              Notes from Honeydone
            </p>
            <p className="text-sm text-blue-800 mt-1 whitespace-pre-wrap">
              {visit.contractor_notes?.trim() ? visit.contractor_notes : 'No notes from Honeydone yet.'}
            </p>
          </div>
          <div className="bg-amber-50 rounded-lg p-3">
            <p className="text-sm font-medium text-amber-900 flex items-center gap-1.5">
              <MessageSquare className="w-4 h-4" />
              Your Provider Notes
            </p>
            <p className="text-xs text-amber-800 mt-1">Add your own notes for Honeydone and the homeowner.</p>
            <textarea
              value={notesDraft}
              onChange={(e) => setNotesDraft(e.target.value)}
              rows={4}
              className="mt-3 w-full px-3 py-2 text-sm border border-amber-200 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-black"
              placeholder="Add visit notes..."
            />
            <div className="mt-3 flex items-center gap-3">
              <button
                type="button"
                onClick={saveContractorNotes}
                disabled={savingNotes}
                className="inline-flex items-center px-3 py-2 rounded-md bg-black text-white text-sm hover:bg-gray-800 disabled:opacity-60"
              >
                {savingNotes ? 'Saving...' : 'Save Notes'}
              </button>
              {notesMessage && (
                <span className={`text-sm ${notesMessageType === 'success' ? 'text-green-700' : 'text-red-700'}`}>
                  {notesMessage}
                </span>
              )}
            </div>
          </div>

          <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
            <p className="text-sm font-medium text-gray-900 mb-3">Add photo</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs uppercase tracking-wide text-gray-500 mb-1">Photo type</label>
                <select
                  value={photoType}
                  onChange={(e) => setPhotoType(e.target.value as VisitPhoto['photo_type'])}
                  disabled={uploadingPhoto}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-sm focus:outline-none focus:ring-1 focus:ring-black disabled:bg-gray-100"
                >
                  <option value="general">General</option>
                  <option value="before">Before</option>
                  <option value="during">During</option>
                  <option value="after">After</option>
                  <option value="issue">Issue</option>
                  <option value="completion">Completion</option>
                </select>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wide text-gray-500 mb-1">Description (optional)</label>
                <input
                  type="text"
                  value={photoDescription}
                  onChange={(e) => setPhotoDescription(e.target.value)}
                  disabled={uploadingPhoto}
                  placeholder="What does this show?"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-sm focus:outline-none focus:ring-1 focus:ring-black disabled:bg-gray-100"
                />
              </div>
            </div>
            <div className="mt-3">
              <label className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-black text-white text-sm cursor-pointer hover:bg-gray-800 transition-colors">
                {uploadingPhoto ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4" />
                    Upload Photo
                  </>
                )}
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handlePhotoUpload}
                  disabled={uploadingPhoto}
                />
              </label>
            </div>
            {uploadMessage && (
              <p className={`mt-3 text-sm ${uploadMessageType === 'success' ? 'text-green-700' : 'text-red-700'}`}>{uploadMessage}</p>
            )}
          </div>

          <div>
            {photos.length === 0 ? (
              <div className="text-sm text-gray-600 flex items-center gap-2">
                <Camera className="w-4 h-4 text-gray-500" />
                No photos uploaded yet.
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {photos.map((photo) => (
                  <div key={photo.id} className="space-y-1">
                    <img src={photo.photo_url} alt={photo.description || 'Visit photo'} className="w-full h-32 object-cover rounded-lg" />
                    <p className="text-xs text-gray-600 capitalize">{photo.photo_type}</p>
                    {photo.description && <p className="text-xs text-gray-500 line-clamp-2">{photo.description}</p>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        <section className="bg-white shadow rounded-xl p-5 sm:p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">Actions (coming soon)</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
              <p className="font-medium text-gray-800">Check in</p>
              <p className="text-xs text-gray-600 mt-1">Start visit timing at arrival.</p>
            </div>
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
              <p className="font-medium text-gray-800">Clock out</p>
              <p className="text-xs text-gray-600 mt-1">Complete the visit and save end time.</p>
            </div>
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
              <p className="font-medium text-gray-800">Add notes</p>
              <p className="text-xs text-gray-600 mt-1">Capture work performed and follow-ups.</p>
            </div>
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
              <p className="font-medium text-gray-800">Add cost</p>
              <p className="text-xs text-gray-600 mt-1">Record visit cost and materials.</p>
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-4 flex items-center gap-1.5">
            <Home className="w-3.5 h-3.5" />
            This page only shows contractor-relevant details and omits internal planning notes.
          </p>
        </section>
      </div>
    </div>
  );
};

export default ContractorVisitDetail;
